import { ConfigFieldValue } from '@/types/automationFields.ts';
import { Center } from '@mantine/core';
import useRetrieveConfigFieldSensitiveValue from '@/hooks/api/integrations/useRetrieveConfigFieldSensitiveValue.ts';
import React, { useCallback, useEffect, useState } from 'react';
import CustomSpinner from '@/components/Spinner.tsx';
import { DispatchStep, TriggerStep } from '@/types/automationSteps.ts';
import useSetConfigFieldSensitiveValue from '@/hooks/api/integrations/useSetConfigFieldSensitiveValue';
import { useDebouncedCallback } from '@mantine/hooks';
import { DEBOUNCE_TIME } from '@/utils/constants.ts';
import IntegrationField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationField.tsx';
import { IntegrationConfigField } from '@/types/integrations.ts';

type props = {
  idx: number;
  step: TriggerStep | DispatchStep;
  stepId: string;
  field: IntegrationConfigField;
  fieldValueConfig: ConfigFieldValue;
  disabled: boolean;
};

const IntegrationFieldSensitive = ({
  idx,
  step,
  stepId,
  field,
  fieldValueConfig,
  disabled,
}: props) => {
  const { data: sensitiveValue, isLoading } = useRetrieveConfigFieldSensitiveValue(
    stepId,
    field.id,
    !disabled,
  );

  const [fieldValue, setFieldValue] = useState<any>(null);

  useEffect(() => {
    if (sensitiveValue) {
      setFieldValue(sensitiveValue.value);
    }
  }, [field.is_sensitive, sensitiveValue]);

  const { mutate: updateValueNoDelay } = useSetConfigFieldSensitiveValue();

  const updateValueDelay = useDebouncedCallback(updateValueNoDelay, { delay: DEBOUNCE_TIME });

  const onFieldChangeDelay = useCallback(
    (fieldId: string, updated_value: any) => {
      setFieldValue(updated_value);
      updateValueDelay({ stepId: step.step_id, fieldId: fieldId, value: updated_value });
    },
    [step.step_id, updateValueDelay],
  );

  const onFieldChangeNoDelay = useCallback(
    (fieldId: string, updated_value: any) => {
      setFieldValue(updated_value);
      updateValueNoDelay({ stepId: step.step_id, fieldId: fieldId, value: updated_value });
    },
    [step.step_id, updateValueNoDelay],
  );

  if (isLoading) {
    return (
      <Center>
        <CustomSpinner />
      </Center>
    );
  }

  return (
    <IntegrationField
      key={field.id + idx.toString()}
      stepId={step.step_id}
      field={field}
      fieldValue={fieldValue}
      fieldValueConfig={fieldValueConfig}
      onChangeDelay={onFieldChangeDelay}
      onChangeNoDelay={onFieldChangeNoDelay}
      disabled={disabled}
    />
  );
};

export default IntegrationFieldSensitive;
